import { LabelSearchTableActionType as Action } from '@aibees/elephant/feature/search-list-card/types';
import {
  useSearchSimilarityFromRoute,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { ROUTES as R } from '@aibees/elephant/router';
import { SearchType } from '@aibees/elephant/types';
import { Label, LabelGrouping, LabelType } from '@aibees/elephant/types/label';

export const usePrepareUrlForSearchType = (tag: LabelType) => {
  const type = useSearchTypeFromRoute();
  const isSimilarity = useSearchSimilarityFromRoute();

  const isCompany = type === SearchType.Company;
  const isPeople = type === SearchType.People;

  const labelKeyMap = [
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Include &&
        item.grouping === LabelGrouping.COMPANY &&
        tg === LabelType.LABEL,
      result: 'labels_account_include',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Include &&
        item.grouping === LabelGrouping.PEOPLE &&
        tg === LabelType.LABEL,
      result: 'labels_contact_include',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Exclude &&
        item.grouping === LabelGrouping.COMPANY &&
        tg === LabelType.LABEL,
      result: 'labels_account_exclude',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Exclude &&
        item.grouping === LabelGrouping.PEOPLE &&
        tg === LabelType.LABEL,
      result: 'labels_contact_exclude',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.IncludeAnd &&
        item.grouping === LabelGrouping.COMPANY &&
        tg === LabelType.LABEL,
      result: 'companies_all_of_labels',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.IncludeAnd &&
        item.grouping === LabelGrouping.PEOPLE &&
        tg === LabelType.LABEL,
      result: 'people_all_of_labels',
    },

    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Include &&
        item.grouping === LabelGrouping.COMPANY &&
        tg === LabelType.LIST,
      result: 'companies_include_list',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Include &&
        item.grouping === LabelGrouping.PEOPLE &&
        tg === LabelType.LIST,
      result: 'people_include_list',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Exclude &&
        item.grouping === LabelGrouping.COMPANY &&
        tg === LabelType.LIST,
      result: 'companies_exclude_list',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.Exclude &&
        item.grouping === LabelGrouping.PEOPLE &&
        tg === LabelType.LIST,
      result: 'people_exclude_list',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.IncludeAnd &&
        item.grouping === LabelGrouping.COMPANY &&
        tg === LabelType.LIST,
      result: 'companies_all_of_list',
    },
    {
      condition: (actionType: Action, item: Label, tg: LabelType) =>
        actionType === Action.IncludeAnd &&
        item.grouping === LabelGrouping.PEOPLE &&
        tg === LabelType.LIST,
      result: 'people_all_of_list',
    },
  ];

  const getLabelKey = (actionType: Action, item: Label) => {
    const labelId = encodeURIComponent(item.id);
    const searchValue = `${item.name}::${labelId}`;
    const baseUrl =
      item.grouping === LabelGrouping.COMPANY
        ? R.searchCompany
        : R.searchPeople;
    const match = labelKeyMap.find(({ condition }) =>
      condition(actionType, item, tag)
    );

    return {
      searchKey: match ? match.result : '',
      url: match?.result && `${baseUrl}?${match.result}=${searchValue}`,
      searchValue,
    };
  };

  const prepareSearchValue = (
    searchKey: string,
    searchValue: string,
    currentParams: Record<string, string | undefined>
  ) => {
    const existingValues = currentParams[searchKey]?.split('^') || [];
    const newValue = existingValues.includes(searchValue)
      ? currentParams[searchKey]
      : searchValue + '^' + (currentParams[searchKey] || '');

    return newValue;
  };

  const isCurrent = (item: Label): boolean => {
    // TODO refactor
    if (isPeople && item.grouping === LabelGrouping.PEOPLE) return true;
    if ((isCompany || isSimilarity) && item.grouping === LabelGrouping.COMPANY)
      return true;
    return false;
  };

  return { getLabelKey, isCurrent, prepareSearchValue };
};
